import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const ContactImport = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.name.endsWith('.csv')) {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/contacts/import/csv', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);
      setFile(null);
      // Reset file input
      document.getElementById('fileInput').value = '';
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-virgo-gold-400">Import Contacts</h1>
          <button
            onClick={() => navigate('/contacts')}
            className="px-4 py-2 text-sm text-virgo-teal-200 hover:text-white"
          >
            ← Back to Contacts
          </button>
        </div>

        <div className="bg-virgo-navy-700 rounded-lg shadow p-6">
          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">CSV Import Instructions</h2>
            <ul className="list-disc list-inside text-sm text-virgo-teal-300 space-y-1">
              <li>Export contacts from Clio as a CSV file</li>
              <li>The CSV should include standard Clio contact fields</li>
              <li>Duplicate contacts (by Clio ID) will be skipped</li>
              <li>Admin permission required</li>
            </ul>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-virgo-teal-200 mb-2">
              Select CSV File
            </label>
            <input
              id="fileInput"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="block w-full text-sm text-virgo-teal-400
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100
                cursor-pointer"
              disabled={importing}
            />
            {file && (
              <p className="mt-2 text-sm text-virgo-teal-300">
                Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {result && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
              <h3 className="font-semibold text-green-900 mb-2">Import Complete!</h3>
              <div className="text-sm text-green-800 space-y-1">
                <p>Total rows processed: {result.total_rows}</p>
                <p>Successfully imported: {result.successful}</p>
                <p>Duplicates skipped: {result.duplicates_skipped}</p>
                <p>Failed: {result.failed}</p>
              </div>
              
              {result.errors && result.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-red-900 mb-2">Errors:</h4>
                  <div className="max-h-40 overflow-y-auto text-xs text-red-800 space-y-1">
                    {result.errors.slice(0, 10).map((err, idx) => (
                      <p key={idx}>Row {err.row}: {err.error}</p>
                    ))}
                    {result.errors.length > 10 && (
                      <p className="italic">...and {result.errors.length - 10} more errors</p>
                    )}
                  </div>
                </div>
              )}
              
              <button
                onClick={() => navigate('/contacts')}
                className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                View Contacts
              </button>
            </div>
          )}

          <button
            onClick={handleImport}
            disabled={!file || importing}
            className={`w-full py-3 px-4 rounded-md font-semibold text-white
              ${!file || importing
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-virgo-teal-600 hover:bg-virgo-teal-700'
              }`}
          >
            {importing ? 'Importing...' : 'Import Contacts'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactImport;
